// ./src/components/features/Dashboard/DashboardVehicleTasks/DashboardVehicleTasks.tsx
// Dashboard widget showing vehicle tasks needing attention:
// overdue, due soon, and in-progress tasks with quick actions.
// Refactored to use DashboardSection + DashboardItemTile reusable components.

import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { getTaskUrgencyOption, getTaskPriorityOption } from "@/config";

import { formatDate } from "@/utils/dateHelpers";
import { getCarDisplayName } from "@/utils/carHelpers";
import { VehicleTask, VEHICLE_TASK_STATUS } from "@/utils/api/VehicleTaskService";

import { useDashboardContext } from "@/contexts/DashboardContext";

import { DashboardSection, CountBadge } from "./DashboardSection";
import {
  DashboardItemTile,
  TileBadge,
  TileAction,
  TileStatusType,
} from "./DashboardItemTile";

// ============================================
// Types
// ============================================

export interface DashboardVehicleTasksProps {
  /** Tasks returned by the dashboard API (overdue, due soon, in progress) */
  tasks: VehicleTask[];

  /** Whether data is loading */
  loading?: boolean;

  /** Callback when a task's complete button is clicked */
  onComplete?: (task: VehicleTask) => void;

  /** Callback when a task's edit button is clicked */
  onEdit?: (task: VehicleTask) => void;

  /** Callback when the "Add Task" button is clicked */
  onCreate?: () => void;

  /** Callback when "View All" button is clicked */
  onViewAll?: () => void;

  /** Whether a complete operation is in progress */
  isCompleting?: boolean;

  /** ID of the task currently being completed (for per-item loading state) */
  completingTaskId?: string | null;

  /** Maximum items to show before "Show More" button (default: 5) */
  initialDisplayCount?: number;
}

// ============================================
// Constants
// ============================================

const DEFAULT_INITIAL_DISPLAY_COUNT = 5;

// ============================================
// Mapping Helpers
// ============================================

/**
 * Map task urgency status to tile badge status type.
 */
const getStatusBadgeType = (
  urgencyStatus: string | null | undefined
): TileBadge | null => {
  switch (urgencyStatus) {
    case "overdue":
      return { type: "overdue" };
    case "due_soon":
      return { type: "due_soon" };
    case "in_progress":
      return { type: "in_progress" };
    case "upcoming":
      return { type: "upcoming" };
    default:
      return null;
  }
};

/**
 * Map task priority value to tile badge priority type.
 */
const getPriorityBadgeType = (
  priority: number | null | undefined
): TileBadge | null => {
  const option = getTaskPriorityOption(priority);
  if (!option) return null;

  switch (option.value) {
    case 300:
      return { type: "high" };
    case 200:
      return { type: "medium" };
    case 100:
      return { type: "low" };
    default:
      return null;
  }
};

/**
 * Map urgency status to tile status type for the bottom text color.
 */
const getStatusType = (
  urgencyStatus: string | null | undefined
): TileStatusType => {
  switch (urgencyStatus) {
    case "overdue":
      return "error";
    case "due_soon":
      return "warning";
    case "in_progress":
      return "info";
    default:
      return "neutral";
  }
};

// ============================================
// Main Component
// ============================================

export const DashboardVehicleTasks: React.FC<DashboardVehicleTasksProps> = ({
  tasks,
  loading = false,
  onComplete,
  onEdit,
  onCreate,
  onViewAll,
  isCompleting = false,
  completingTaskId = null,
  initialDisplayCount = DEFAULT_INITIAL_DISPLAY_COUNT,
}) => {
  const { t } = useTranslation(["vehicleTasks", "common"]);
  const { getCarById } = useDashboardContext();

  // Count by urgency for header badges
  const countBadges = useMemo<CountBadge[]>(() => {
    const counts = { overdue: 0, due_soon: 0, in_progress: 0 };
    tasks.forEach((task) => {
      if (task.urgencyStatus === "overdue") counts.overdue++;
      else if (task.urgencyStatus === "due_soon") counts.due_soon++;
      else if (task.urgencyStatus === "in_progress") counts.in_progress++;
    });

    const badges: CountBadge[] = [];
    if (counts.overdue > 0) badges.push({ type: "error", count: counts.overdue });
    if (counts.due_soon > 0) badges.push({ type: "warning", count: counts.due_soon });
    if (counts.in_progress > 0) badges.push({ type: "info", count: counts.in_progress });
    return badges;
  }, [tasks]);

  // Build subtitle for a task: "Vehicle Name · Due Date"
  const getSubtitle = useCallback(
    (task: VehicleTask): string => {
      const parts: string[] = [];

      const vehicle = task.car || (task.carId ? getCarById(task.carId) : null);
      if (vehicle) {
        parts.push(getCarDisplayName(vehicle));
      }

      if (task.dueDate) {
        parts.push(formatDate(task.dueDate));
      }

      return parts.join(" · ");
    },
    [getCarById]
  );

  // Build status text for a task
  const getStatusText = useCallback(
    (task: VehicleTask): string | undefined => {
      if (task.daysUntilDue == null) {
        if (task.status === VEHICLE_TASK_STATUS.IN_PROGRESS) {
          return t("vehicleTasks:dashboard.inProgress");
        }
        return undefined;
      }

      if (task.daysUntilDue < 0) {
        return t("vehicleTasks:dashboard.overdueDays", {
          count: Math.abs(task.daysUntilDue),
        });
      }

      if (task.daysUntilDue === 0) {
        return t("vehicleTasks:dashboard.dueToday");
      }

      return t("vehicleTasks:dashboard.dueInDays", {
        count: task.daysUntilDue,
      });
    },
    [t]
  );

  // Build icon info from urgency
  const getIconInfo = useCallback(
    (task: VehicleTask): { iconName: string; iconColor: string } => {
      const option = getTaskUrgencyOption(task.urgencyStatus ?? undefined);
      return {
        iconName: option?.icon || "ListTodo",
        iconColor: option?.color || "gray",
      };
    },
    []
  );

  // Build badges array for a task
  const getBadges = useCallback((task: VehicleTask): TileBadge[] => {
    const badges: TileBadge[] = [];

    const statusBadge = getStatusBadgeType(task.urgencyStatus);
    if (statusBadge) badges.push(statusBadge);

    const priorityBadge = getPriorityBadgeType(task.priority);
    if (priorityBadge) badges.push(priorityBadge);

    return badges;
  }, []);

  // Build actions array for a task
  const getActions = useCallback(
    (task: VehicleTask): TileAction[] => {
      const actions: TileAction[] = [];

      if (onComplete && task.status !== VEHICLE_TASK_STATUS.COMPLETE) {
        actions.push({
          type: "complete",
          onClick: () => onComplete(task),
          loading: isCompleting && completingTaskId === task.id,
          tooltip: t("vehicleTasks:dashboard.completeTask"),
        });
      }

      if (onEdit) {
        actions.push({
          type: "edit",
          onClick: () => onEdit(task),
          tooltip: t("vehicleTasks:dashboard.editTask"),
        });
      }

      return actions;
    },
    [onComplete, onEdit, isCompleting, completingTaskId, t]
  );

  return (
    <DashboardSection
      title={t("vehicleTasks:dashboard.title")}
      countBadges={countBadges}
      headerAction={
        onViewAll
          ? {
            label: t("vehicleTasks:dashboard.viewAll"),
            onClick: onViewAll,
          }
          : undefined
      }
      addAction={
        onCreate
          ? {
            label: t("vehicleTasks:toolbar.add"),
            onClick: onCreate,
          }
          : undefined
      }
      loading={loading}
      totalItems={tasks.length}
      initialDisplayCount={initialDisplayCount}
      emptyState={{
        //title: t("vehicleTasks:dashboard.emptyTitle"),
        //iconName: "ListCheck",
        description: t("vehicleTasks:dashboard.emptyDescription"),

      }}
    >
      {tasks.map((task) => {
        const { iconName, iconColor } = getIconInfo(task);
        return (
          <DashboardItemTile
            key={task.id || task.carId}
            title={task.title || t("vehicleTasks:dashboard.untitled")}
            iconName={iconName}
            iconColor={iconColor}
            badges={getBadges(task)}
            subtitle={getSubtitle(task)}
            statusText={getStatusText(task)}
            statusType={getStatusType(task.urgencyStatus)}
            actions={getActions(task)}
          />
        );
      })}
    </DashboardSection>
  );
};